import type { z } from "zod";
import { ListUserHistoryContract } from "./ListUserHistoryContract";
import { infiniteQueryOptions, keepPreviousData } from "@tanstack/react-query";
import { getCommonFetchHeaders } from "@/common/getCommonFetchHeaders";
import { HttpStatus } from "@/common/contracts";

export const listUserHistoryInfiniteQueryOptionsMocks = {
	error: "list-user-history-error",
	slowdown: "list-user-history-slowdown",
	noResponse: "list-user-history-no-response",
	shortResponse: "list-user-history-short-response",
	longResponse: "list-user-history-long-response",
} as const;

export function listUserHistoryInfiniteQueryOptionsKey(
	params?: z.infer<typeof ListUserHistoryContract.params>,
	search?: Omit<z.infer<typeof ListUserHistoryContract.search>, "page">,
) {
	return [ListUserHistoryContract.path, params, search].filter(Boolean);
}

export function listUserHistoryInfiniteQueryOptions(
	params: z.infer<typeof ListUserHistoryContract.params>,
	search?: Omit<z.infer<typeof ListUserHistoryContract.search>, "page">,
) {
	return infiniteQueryOptions({
		queryKey: listUserHistoryInfiniteQueryOptionsKey(params, search),
		queryFn: async ({ pageParam, signal }) => {
			return fetch(
				`${import.meta.env.VITE_API_URL}/api${ListUserHistoryContract.serializePath(params, { ...search, page: pageParam })}`,
				{ headers: await getCommonFetchHeaders(), signal },
			).then(async (res) => {
				const result = ListUserHistoryContract.response.parse({
					status: res.status,
					body: await res.json(),
				});
				if (result.status === HttpStatus.Success) {
					return result.body;
				}
				throw new Error(result.body.message);
			});
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage, _allPages, lastPageParam) => {
			if (lastPage.page < lastPage.totalPages) {
				return lastPageParam + 1;
			}
			return undefined;
		},
		placeholderData: keepPreviousData,
		retry: 1,
	});
}
